import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import QRCode from 'react-qr-code';
import { useSelector } from 'react-redux';

MaintainerChargeConnectorListView.propTypes = {
  chargePointDetail: PropTypes.object,
  items: PropTypes.array,
  onRefresh: PropTypes.func,
  hasAppTab: PropTypes.bool,
};

function MaintainerChargeConnectorListView({ chargePointDetail, items, onRefresh, hasAppTab = false }) {
  // MARK: ---- PROPERTIES ----
  const { user } = useSelector((state) => state.auth);
  const chargeConnectorColumns = useMemo(() => {
    return [
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Mã QR
          </span>
        ),
        selector: (row) => (
          <div
            className="p-2 cursor-pointer hover-opacity-60"
            onClick={() => {
              handleShowQRCode(row);
            }}
          >
            <QRCode value={ChargeHelper.Utils.getChargeConnectorQRCodeLink(row)} size={34} />
          </div>
        ),
        width: '70px',
        center: true,
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Tên vòi sạc
          </span>
        ),
        selector: (row) => {
          return `Vòi sạc ${row?.connectorNumber}`;
        },
        minWidth: '100px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Mã vòi sạc
          </span>
        ),
        selector: (row) => row?.connectorCode,
        minWidth: '120px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Loại vòi sạc
          </span>
        ),
        selector: (row) => row?.type?.length ? ChargeHelper?.Utils?.getChargeConnectorTypeName(row?.type) : '',
        minWidth: '100px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Serial
          </span>
        ),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.connectorSerial ?? 'Chưa thiết lập'}>
            <span> {row?.connectorSerial ?? 'Chưa thiết lập'}  </span>
          </KTTooltip>
        ),
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <span
            className="letter-spacing-unset"
            style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
          >
            Trạng thái
          </span>
        ),
        selector: (row) => {
          return (
            <KTLabel
              text={ChargeHelper.Utils.getChargeConnectorStatusName(row?.status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getChargeConnectorStatusColor(
                row?.status
              )}`}
            />
          );
        },
        minWidth: '160px',
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Tổng công suất hiện tại
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              kW
            </span>
          </div>
        ),
        selector: (row) =>
          row?.currentTotalPower ? (row?.currentTotalPower / 1000).toFixed(2) : '',
        minWidth: '180px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        name: (
          <div className="d-flex flex-column align-items-end">
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('600', '12px', AppColors.gray[900])}
            >
              Nhiệt độ
            </span>
            <span
              className="letter-spacing-unset"
              style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[700])}
            >
              °C
            </span>
          </div>
        ),
        selector: (row) => row?.temperature,
        minWidth: '120px',
        right: true,
        style: tableStyle.columns.normal,
      },
    ];
  }, [user, chargePointDetail]);

  // MARK: ---- METHODS ----
  const handleShowQRCode = (row) => {
    AppCustomDialogHelper.show(
      `Vòi sạc ${row?.connectorNumber}`,
      <div className="p-4">
        <QRCode value={ChargeHelper.Utils.getChargeConnectorQRCodeLink(row)} size={'100%'} />
      </div>,
      'md',
      true,
      true
    );
  };

  return (
    <div className="px-4">
      {UIHelper.buildCard(
        <div className="pb-5">
          {/* header */}
          <div className="px-5 pt-5 pb-3 d-flex align-items-center justify-content-between">
            <div className="d-flex flex-column">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                Danh sách vòi sạc
              </span>
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                Tổng:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                  {items?.length} vòi sạc
                </span>
              </span>
            </div>
          </div>

          {/* table */}
          <div className="px-5">
            <KTTable
              columns={chargeConnectorColumns}
              items={items}
              selectableRows={false}
              striped={false}
              compactGrid={true}
              onClickedRow={(row) => { }}
              hasActionColumn={false}
              onSelectedRowsChanged={(rows) => { }}
              onDoubleClickedRow={(row) => { }}
              noDataComponent={UIHelper.buildEmptyNoData(
                'Trụ sạc này chưa có vòi sạc nào được kết nối.'
              )}
            />
          </div>
        </div>,
        false,
        false,
        true,
        false,
        AppColors.gray[300],
        hasAppTab
      )}
    </div>
  );
}

export default MaintainerChargeConnectorListView;
